import { Trans, useLingui } from '@lingui/react/macro'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'designSystem/components/AlertDialog'
import { Text } from 'designSystem/components/Text'
import type { ChatHistoryBulkAction } from 'features/embeddedSidebar/sessions/chatHistoryBulkActions'
import type { MouseEvent } from 'react'

interface ChatHistoryBulkActionDialogProps {
  action: ChatHistoryBulkAction
  hasError: boolean
  isWorking: boolean
  onConfirm: (event: MouseEvent<HTMLButtonElement>) => Promise<void>
  onOpenChange: (open: boolean) => void
}

export function ChatHistoryBulkActionDialog({
  action,
  hasError,
  isWorking,
  onConfirm,
  onOpenChange,
}: ChatHistoryBulkActionDialogProps) {
  const { t } = useLingui()
  const isDeleting = action === 'delete'
  const isUnarchiving = action === 'unarchive'
  const confirmLabel = isDeleting
    ? t`Delete chats`
    : isUnarchiving
      ? t`Unarchive chats`
      : t`Archive chats`
  const progressLabel = isDeleting
    ? t`Deleting…`
    : isUnarchiving
      ? t`Unarchiving…`
      : t`Archiving…`
  const title = isDeleting
    ? t`Delete archived chats?`
    : isUnarchiving
      ? t`Unarchive all chats?`
      : t`Archive recent chats?`
  const errorMessage = isDeleting
    ? t`Could not delete archived chats. Please try again.`
    : isUnarchiving
      ? t`Could not unarchive chats. Please try again.`
      : t`Could not archive recent chats. Please try again.`

  return (
    <AlertDialog open onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>
            {isDeleting ? (
              <Trans>
                Delete your archived chats for this site from your history and
                close their open tabs. Pinned chats, running replies, and chats
                with unsent drafts will stay. Recent chats will stay. Deleted
                chats cannot be restored.
              </Trans>
            ) : isUnarchiving ? (
              <Trans>
                Move all your archived chats for this site back to Recent chats.
                Open tabs and unsent drafts will stay. You can archive these
                chats again later.
              </Trans>
            ) : (
              <Trans>
                Move your recent chats for this site to Archived chats and close
                their open tabs. Pinned chats, running replies, and chats with
                unsent drafts will stay. You can restore archived chats later.
              </Trans>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {hasError ? (
          <Text role='alert' variant='caption' color='destructive'>
            {errorMessage}
          </Text>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isWorking}>
            <Trans>Cancel</Trans>
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={isWorking}
            variant={isDeleting ? 'destructive' : 'default'}
            onClick={onConfirm}>
            {isWorking ? progressLabel : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
